import Modal from '@mui/material/Modal';
import { Link } from 'react-router-dom';
import { Fragment, useEffect, useMemo } from 'react';
import { LockOutlined } from '@mui/icons-material';
import { useSelector, useDispatch } from "react-redux";

import { showMessage } from '../../config.js';
import ModalWrapper from '../common/Modal/ModalWrapper';
import { toggleModal } from "../../reducers/modalReducer";

export default function CoreWorkspaceButton({ buttonClass, linearProgress, permissionLevel, step, title, type, seeMore, howItWorks, handleSeeMore, handleHowItWorks, directors, directorsStats, company, moduleCertification,
	module, setTab, getFitInvestorFirms, scrollId, setModuleTab, setIsViewTwoMinuteReport, isReady = false, fitInvestorFirms, subscription, time, isBuy, price, information }) {
	const dispatch = useDispatch();
	const user = useSelector(state => state.user);

	const { strongCount, mediumCount, lowCount } = useMemo(() => {
		if (!fitInvestorFirms?.length) return { strongCount: 0, mediumCount: 0, lowCount: 0 };

		return fitInvestorFirms.reduce((acc, investor) => {
			const score = investor.fitScorePercentage;
			if (score >= 83) acc.strongCount++;
			else if (score >= 66.6 && score < 83) acc.mediumCount++;
			else if (score < 66.6) acc.lowCount++;
			return acc;
		}, { strongCount: 0, mediumCount: 0, lowCount: 0 });
	}, [fitInvestorFirms]);

	const handleLinkModuleTab = (type, module) => {
		if ((type.startsWith('two-minute-') && !company['freeModules']) || (company['freeModules'] && !company['freeModules'][type])) {
			return `#`;
		}

		if (user['role'] == 'Member' && company['creatorId']['_id'] != user['_id']) {
			if (['software-development', 'esg', 'warranty-disclosure', 'directors-questionnaire'].includes(type) && !company?.['memberPermissions']?.[user['_id']]?.[module?.['sections']?.[0]?.['sectionNo']]) return '#';
		}
		if (permissionLevel == 'ALL') {
			if (user?.['role'] == 'Guest' && (type == `two-minute-test-${company?.['testType']}` || type == "kpi-report")) return '#';
			if (type == 'directors-questionnaire') return '#';
			return `#${type}`;
		}
	};

	useEffect(() => {
		if (type === "kpi-report") {
			setIsViewTwoMinuteReport(false);
		}
	}, [type]);

	const setReportTab = async (type) => {
		if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD')) {
			showMessage('You have Dashboard access only!');
			return;
		};

		if (type == 'investor-lead') {
			getFitInvestorFirms();
			return;
		}

		setTab(type + '-report')
	};

	const showPercent = (value, type, title) => {
		let color;
		if (value > 0) {
			if (type == 'Module Risk') color = value > 67 ? 'red' : value > 33 ? 'yellow' : 'green';
			else if (type == 'Module Complete') color = value > 67 ? 'green' : value > 33 ? 'yellow' : 'red';
		}
		else color = 'c28'
		return <h4 className={`${color} fs2rem lh1 ${title === "InvestorMatch" ? 'mr0rem' : 'mr2rem'}`}>{value || 0}%<br /><span className={`${value > 0 ? 'c15 ' : 'c28 '} fs0-75rem w4rem db fw300`}>{type}</span></h4>
	};

	const navigateSection = async (type, sectionNo) => {
		if (user?.['role'] == 'Guest') {
			dispatch(toggleModal({ open: 'noRegistrationReport' }))
			return;
		};

		if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD' || permissionLevel == 'DASHBOARD & REPORT')) {
			showMessage(`You have ${permissionLevel} access only!`);
			return;
		};

		if (['software-development', 'esg', 'warranty-disclosure', 'directors-questionnaire'].includes(type)) {
			// CHECK EXPANSION PACK PERMISSIONS FOR MEMBERS
			const message = `You don't have permission for this module. Please contact your admin ${company['creatorId']['firstName'] ? `${company['creatorId']['firstName']} ${company['creatorId']['lastName'] ? company['creatorId']['lastName'] : ""}` : 'user'} to gain access`
			if (user['role'] == 'Member' && company['creatorId']['_id'] != user['_id']) {
				if (!(company?.['memberPermissions']?.[user['_id']]?.[module?.['sections']?.[0]?.['sectionNo']])) {
					showMessage(message);
					return;
				}
			}
			// CHECK EXPANSION PACK PERMISSIONS FOR ADVISORS/INVESTORS
			if ((user['role'] == "Advisor" || user['role'] == 'Investor') && company['creatorId']['_id'] != user['_id']) {
				if (!(company?.[user['role'] == "Investor" ? 'investors' : 'advisors']?.[user['_id']]?.['modules']?.[module?.['sections']?.[0]?.['sectionNo']])) {
					showMessage(message);
					return;
				}
			}
		};

		await setTab(type);
		setTimeout(() => {
			let element = document.getElementById(sectionNo);
			if (element) element.scrollIntoView({ behavior: 'instant' }, true);
		}, 1000);
	};

	const getHelpLink = (type) => {
		if (type.startsWith('two-minute-test-')) {
			return 'https://intercom.help/diligentsia-limited/en/collections/6922239-2-minute-test-from-dashboard';
		}

		if (type == 'kpi-report') {
			return 'https://intercom.help/diligentsia-limited/en/articles/8557593-key-performance-indicator-kpi-reporting';
		}


		if (type == 'repository') {
			return 'https://intercom.help/diligentsia-limited/en/collections/6737385-your-diligentsia-data-repository';
		}

		if (type.startsWith('business-audit-')) {
			return 'https://intercom.help/diligentsia-limited/en/collections/6775860-your-business-audit';
		}

		if (type == 'unaudited-certification') {
			return 'https://intercom.help/diligentsia-limited/en/collections/6775868-your-certification';
		}

		if (type == 'warranty-disclosure') {
			return 'https://intercom.help/diligentsia-limited/en/articles/8557715-expansion-pack-warranty-disclosure-module';
		}

		if (type == 'directors-questionnaire') {
			return 'https://intercom.help/diligentsia-limited/en/articles/8557468-directors-questionnaire-module';
		}

		if (type == 'software-development') {
			return 'https://intercom.help/diligentsia-limited/en/articles/8557691-expansion-pack-software-development';
		}

		if (type == 'esg') {
			return 'https://intercom.help/diligentsia-limited/en/articles/8557477-expansion-pack-environmental-social-governmental-esg';
		}

		return '';
	};

	const moduleTooltip = (sections, module) => {
		return (
			<div className='cp module-tooltip-box'>
				<div className='hover-list'>
					<img src={require('../../../images/hover-list-icon.png')} className="pa" alt="list-img" />
				</div>

				{sections.length > 0 &&
					<span className={`tooltip-area ${(module?.heading === "Investor Match" || (sections.length && sections.length < 8))
						? 'tooltip-top-important'
						: ''
						}`}
					>
						{sections.map((section, idx) => {
							if (!section.stats) section.stats = module['sections'][0]['stats'];
							const completeness = section.stats.completionPercent;

							let riskColour;
							if (module.type === "repository" || section.stats.riskPercent < 38) riskColour = 7;
							else if (section.stats.riskPercent > 62) riskColour = 9;
							else riskColour = 8;

							let hexColour = riskColour === 8 ? "#ddb64044" : riskColour === 9 ? "#dd404044" : "#40dda144";

							let colorChange = false;
							if (module['type'] == "warranty-disclosure") {
								if (section?.questions && section?.questions.length > 0) section?.questions.map(question => {
									if (question['answer']['radioOption'] == 'Disclosure' || question['answer']['radioOption'] == 'Not Sure') {
										colorChange = true;
										return;
									}
								})
							}
							return <button key={idx} onClick={() => navigateSection(type, section['sectionNo'])} style={{ background: `linear-gradient(90deg, ${colorChange ? "#f9922a" : hexColour} 0%, ${colorChange ? "#f9922a" : hexColour} ${completeness}%, #f0f0f8 ${completeness}%)` }} data-scroll-to={section['sectionNo']} className="overlay-box tal w100 tac cp c3-hover"><span>{section['sectionName']}</span></button>
						})}
					</span>
				}
			</div>
		)
	};

	const directorModuleTooltip = (items) => {
		return (
			<div className='module-tooltip-box'>
				{items.length > 0 &&
					<span className="tooltip-area">
						{items.map((item, idx) => {
							let completeness = (company['moduleStats']?.[`directors-questionnaire_${item?._id}`]?.['stats']?.['completionPercent'] ? company['moduleStats']?.[`directors-questionnaire_${item?._id}`]?.['stats']?.['completionPercent'] : 0);
							let riskColour;
							if (company['moduleStats']?.[`directors-questionnaire_${item?._id}`]?.['stats']?.['riskPercent'] && company['moduleStats']?.[`directors-questionnaire_${item?._id}`]?.['stats']?.['riskPercent'] < 38) riskColour = 7;
							else if (company['moduleStats']?.[`directors-questionnaire_${item?._id}`]['stats']?.['riskPercent'] && company['moduleStats']?.[`directors-questionnaire_${item?._id}`]['stats']?.['riskPercent'] > 62) riskColour = 9;
							else riskColour = 8;

							let hexColour = riskColour === 8 ? "#ddb64044" : riskColour === 9 ? "#dd404044" : "#40dda144";

							return <div key={idx} style={{ background: `linear-gradient(90deg, ${hexColour} 0%, ${hexColour} ${completeness}%, #f0f0f8 ${completeness}%)` }} className="overlay-box tal"><span>{item['name']}</span></div>
						})}
					</span>
				}
			</div>
		)
	};

	return (
		<Fragment>
			<div id={scrollId} className={`${buttonClass} ${user?.['role'] == 'Guest' ? 'bo3' : 'bo7'}  w100 keyfeature-wihte-item bg1 new-shadow br15px p2rem pr bsbb`}>
				<div className='right-info-hyper-icon'>
					{seeMore &&
						<div className='info-icon pr cp ml0-5rem' onClick={() => handleSeeMore(seeMore)}>
							<svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="13.5" cy="13.5" r="13.5" transform="matrix(1 0 0 -1 0 27)" fill="#59B8FD" />
								<path d="M12.1581 11.0172C12.1652 10.5566 12.5413 10.1875 13.0019 10.1875C13.4624 10.1875 13.8386 10.5602 13.8456 11.0172L14.1269 18.5969C14.1445 18.8852 14.039 19.1664 13.8351 19.3844C13.6171 19.6164 13.3148 19.75 13.0019 19.75C12.689 19.75 12.3867 19.6164 12.1687 19.3844C11.9648 19.1664 11.8593 18.8852 11.8769 18.5969L12.1581 11.0172Z" fill="white" />
								<path d="M13 6.25C13.6213 6.25 14.125 6.75368 14.125 7.375C14.125 7.99632 13.6213 8.5 13 8.5C12.3787 8.5 11.875 7.99632 11.875 7.375C11.875 6.75368 12.3787 6.25 13 6.25Z" fill="white" />
							</svg>
							<div className='overlay-box'>
								<p className='c15 df aic'>
									What is it?
									<i className='icon ml0-5rem'>
										<svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M11.1659 7.57457V10.6723C11.1659 12.3866 9.77122 13.7812 8.05696 13.7812H6.37657C5.23568 13.7812 4.25418 13.2147 3.68373 12.2268L1.77282 8.91715C1.58918 8.5978 1.69863 8.18893 2.01661 8.00543C2.73387 7.59183 3.61212 7.81624 4.0161 8.51635L4.50071 9.35517V4.7025C4.50071 4.15516 4.94565 3.70986 5.49252 3.70986C6.0394 3.70986 6.48516 4.15516 6.48516 4.7025V6.49204C6.65337 6.36068 6.86477 6.28236 7.09416 6.28236C7.42275 6.28236 7.71453 6.44282 7.8953 6.68954C8.07593 6.44282 8.36747 6.28236 8.69579 6.28236C9.10548 6.28236 9.45802 6.53185 9.6094 6.88688C9.7748 6.7358 9.9947 6.6436 10.2357 6.6436C10.7486 6.6436 11.1659 7.06125 11.1659 7.57457ZM5.90232 2.44038V0.628906C5.90232 0.402391 5.71868 0.21875 5.49217 0.21875C5.26565 0.21875 5.08201 0.402391 5.08201 0.628906V2.44035C5.08201 2.66686 5.26565 2.8505 5.49217 2.8505C5.71868 2.8505 5.90232 2.66689 5.90232 2.44038ZM3.46769 4.87501C3.46769 4.64849 3.28405 4.46485 3.05753 4.46485H1.24609C1.01958 4.46485 0.835938 4.64849 0.835938 4.87501C0.835938 5.10152 1.01958 5.28516 1.24609 5.28516H3.05753C3.28405 5.28516 3.46769 5.10152 3.46769 4.87501ZM10.1492 4.87501C10.1492 4.64849 9.96558 4.46485 9.73906 4.46485H7.92759C7.70108 4.46485 7.51744 4.64849 7.51744 4.87501C7.51744 5.10152 7.70108 5.28516 7.92759 5.28516H9.73906C9.96558 5.28516 10.1492 5.10152 10.1492 4.87501ZM4.23261 3.61648C4.39287 3.45639 4.39298 3.19668 4.23288 3.03641L3.45079 2.25354C3.2907 2.0933 3.03098 2.09316 2.87072 2.25326C2.71046 2.41336 2.71035 2.67307 2.87045 2.83333L3.65254 3.61621C3.73265 3.69638 3.83765 3.7365 3.94271 3.7365C4.04776 3.7365 4.15254 3.69646 4.23261 3.61648ZM7.3318 3.61621L8.11389 2.83333C8.27398 2.67307 8.27385 2.41336 8.11361 2.25326C7.95335 2.09314 7.69364 2.0933 7.53354 2.25354L6.75146 3.03641C6.59136 3.19668 6.5915 3.45639 6.75173 3.61648C6.83182 3.69649 6.93671 3.73647 7.04163 3.73647C7.14655 3.73647 7.25171 3.69641 7.3318 3.61621Z" fill="#505050" />
										</svg>
									</i>
								</p>
							</div>
						</div>
					}
					{howItWorks &&
						<div className='hover-list-icon pr cp ml0-5rem' onClick={() => handleHowItWorks(howItWorks)}>
							<svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="13.5" cy="13.5" r="13.5" fill="#59B8FD" />
								<path d="M12.5822 17.454C12.314 17.454 12.0971 17.2371 12.0971 16.9689C12.0971 16.3344 12.1871 15.7863 12.3662 15.3255C12.4976 14.9781 12.7109 14.628 13.0043 14.2743C13.2203 14.0169 13.6073 13.6407 14.1671 13.1475C14.7269 12.6534 15.0905 12.2601 15.2588 11.9667C15.4271 11.6733 15.5099 11.3529 15.5099 11.0055C15.5099 10.3764 15.2642 9.82469 14.7737 9.34859C14.2832 8.87249 13.6811 8.63489 12.9683 8.63489C12.2798 8.63489 11.7047 8.85089 11.2439 9.28199C10.9073 9.59699 10.6544 10.0416 10.487 10.6158C10.3718 11.01 9.9875 11.262 9.5798 11.2134C9.0632 11.1522 8.723 10.641 8.8706 10.1415C9.0974 9.37379 9.4772 8.75549 10.0091 8.28569C10.748 7.63139 11.7254 7.30469 12.9413 7.30469C14.2283 7.30469 15.2552 7.65479 16.022 8.35589C16.7888 9.05699 17.1713 9.90389 17.1713 10.8975C17.1713 11.4726 17.0363 12.0027 16.7672 12.4869C16.4981 12.972 15.9707 13.5615 15.1868 14.2563C14.6603 14.7234 14.3156 15.0681 14.1536 15.2895C13.9916 15.5109 13.8719 15.7656 13.7945 16.0527C13.7369 16.266 13.6973 16.5792 13.6757 16.9905C13.6622 17.2497 13.4471 17.4531 13.1879 17.4531H12.5822V17.454ZM12.0071 19.776C12.0071 19.2675 12.4193 18.8553 12.9278 18.8553C13.4363 18.8553 13.8485 19.2675 13.8485 19.776C13.8485 20.2845 13.4363 20.6967 12.9278 20.6967C12.4193 20.6967 12.0071 20.2845 12.0071 19.776Z" fill="white" />
							</svg>
							<div className='overlay-box'>
								<p className='c15 df aic'>
									How does it work?
									<i className='icon ml0-5rem'>
										<svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M11.1659 7.57457V10.6723C11.1659 12.3866 9.77122 13.7812 8.05696 13.7812H6.37657C5.23568 13.7812 4.25418 13.2147 3.68373 12.2268L1.77282 8.91715C1.58918 8.5978 1.69863 8.18893 2.01661 8.00543C2.73387 7.59183 3.61212 7.81624 4.0161 8.51635L4.50071 9.35517V4.7025C4.50071 4.15516 4.94565 3.70986 5.49252 3.70986C6.0394 3.70986 6.48516 4.15516 6.48516 4.7025V6.49204C6.65337 6.36068 6.86477 6.28236 7.09416 6.28236C7.42275 6.28236 7.71453 6.44282 7.8953 6.68954C8.07593 6.44282 8.36747 6.28236 8.69579 6.28236C9.10548 6.28236 9.45802 6.53185 9.6094 6.88688C9.7748 6.7358 9.9947 6.6436 10.2357 6.6436C10.7486 6.6436 11.1659 7.06125 11.1659 7.57457ZM5.90232 2.44038V0.628906C5.90232 0.402391 5.71868 0.21875 5.49217 0.21875C5.26565 0.21875 5.08201 0.402391 5.08201 0.628906V2.44035C5.08201 2.66686 5.26565 2.8505 5.49217 2.8505C5.71868 2.8505 5.90232 2.66689 5.90232 2.44038ZM3.46769 4.87501C3.46769 4.64849 3.28405 4.46485 3.05753 4.46485H1.24609C1.01958 4.46485 0.835938 4.64849 0.835938 4.87501C0.835938 5.10152 1.01958 5.28516 1.24609 5.28516H3.05753C3.28405 5.28516 3.46769 5.10152 3.46769 4.87501ZM10.1492 4.87501C10.1492 4.64849 9.96558 4.46485 9.73906 4.46485H7.92759C7.70108 4.46485 7.51744 4.64849 7.51744 4.87501C7.51744 5.10152 7.70108 5.28516 7.92759 5.28516H9.73906C9.96558 5.28516 10.1492 5.10152 10.1492 4.87501ZM4.23261 3.61648C4.39287 3.45639 4.39298 3.19668 4.23288 3.03641L3.45079 2.25354C3.2907 2.0933 3.03098 2.09316 2.87072 2.25326C2.71046 2.41336 2.71035 2.67307 2.87045 2.83333L3.65254 3.61621C3.73265 3.69638 3.83765 3.7365 3.94271 3.7365C4.04776 3.7365 4.15254 3.69646 4.23261 3.61648ZM7.3318 3.61621L8.11389 2.83333C8.27398 2.67307 8.27385 2.41336 8.11361 2.25326C7.95335 2.09314 7.69364 2.0933 7.53354 2.25354L6.75146 3.03641C6.59136 3.19668 6.5915 3.45639 6.75173 3.61648C6.83182 3.69649 6.93671 3.73647 7.04163 3.73647C7.14655 3.73647 7.25171 3.69641 7.3318 3.61621Z" fill="#505050" />
										</svg>
									</i>
								</p>
							</div>
						</div>
					}
					{getHelpLink(type) != '' &&
						<Link to={getHelpLink(type)} target='_blank' className='questionmark-icon pr ml0-5rem'>
							<svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="13.5" cy="13.5" r="13.5" fill="#FC7785" />
								<path d="M6.80001 15.7992C7.79413 15.7992 8.60003 14.9934 8.60003 13.9992C8.60003 13.0051 7.79413 12.1992 6.80001 12.1992C5.80589 12.1992 5 13.0051 5 13.9992C5 14.9934 5.80589 15.7992 6.80001 15.7992Z" fill="white" />
								<path d="M12.9992 15.7992C13.9934 15.7992 14.7992 14.9934 14.7992 13.9992C14.7992 13.0051 13.9934 12.1992 12.9992 12.1992C12.0051 12.1992 11.1992 13.0051 11.1992 13.9992C11.1992 14.9934 12.0051 15.7992 12.9992 15.7992Z" fill="white" />
								<path d="M19.2004 15.7992C20.1945 15.7992 21.0004 14.9934 21.0004 13.9992C21.0004 13.0051 20.1945 12.1992 19.2004 12.1992C18.2063 12.1992 17.4004 13.0051 17.4004 13.9992C17.4004 14.9934 18.2063 15.7992 19.2004 15.7992Z" fill="white" />
							</svg>
							{/* <div className='overlay-box'><p className='c15'>Access detailed help pages and tell our AI Assistants what you require help with</p></div> */}
							<div className='overlay-box'>
								<p className='c15 df aic'>
									Detailed help pages or ask our AI Assistants for help
									<i className='icon ml0-5rem'>
										<svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M11.1659 7.57457V10.6723C11.1659 12.3866 9.77122 13.7812 8.05696 13.7812H6.37657C5.23568 13.7812 4.25418 13.2147 3.68373 12.2268L1.77282 8.91715C1.58918 8.5978 1.69863 8.18893 2.01661 8.00543C2.73387 7.59183 3.61212 7.81624 4.0161 8.51635L4.50071 9.35517V4.7025C4.50071 4.15516 4.94565 3.70986 5.49252 3.70986C6.0394 3.70986 6.48516 4.15516 6.48516 4.7025V6.49204C6.65337 6.36068 6.86477 6.28236 7.09416 6.28236C7.42275 6.28236 7.71453 6.44282 7.8953 6.68954C8.07593 6.44282 8.36747 6.28236 8.69579 6.28236C9.10548 6.28236 9.45802 6.53185 9.6094 6.88688C9.7748 6.7358 9.9947 6.6436 10.2357 6.6436C10.7486 6.6436 11.1659 7.06125 11.1659 7.57457ZM5.90232 2.44038V0.628906C5.90232 0.402391 5.71868 0.21875 5.49217 0.21875C5.26565 0.21875 5.08201 0.402391 5.08201 0.628906V2.44035C5.08201 2.66686 5.26565 2.8505 5.49217 2.8505C5.71868 2.8505 5.90232 2.66689 5.90232 2.44038ZM3.46769 4.87501C3.46769 4.64849 3.28405 4.46485 3.05753 4.46485H1.24609C1.01958 4.46485 0.835938 4.64849 0.835938 4.87501C0.835938 5.10152 1.01958 5.28516 1.24609 5.28516H3.05753C3.28405 5.28516 3.46769 5.10152 3.46769 4.87501ZM10.1492 4.87501C10.1492 4.64849 9.96558 4.46485 9.73906 4.46485H7.92759C7.70108 4.46485 7.51744 4.64849 7.51744 4.87501C7.51744 5.10152 7.70108 5.28516 7.92759 5.28516H9.73906C9.96558 5.28516 10.1492 5.10152 10.1492 4.87501ZM4.23261 3.61648C4.39287 3.45639 4.39298 3.19668 4.23288 3.03641L3.45079 2.25354C3.2907 2.0933 3.03098 2.09316 2.87072 2.25326C2.71046 2.41336 2.71035 2.67307 2.87045 2.83333L3.65254 3.61621C3.73265 3.69638 3.83765 3.7365 3.94271 3.7365C4.04776 3.7365 4.15254 3.69646 4.23261 3.61648ZM7.3318 3.61621L8.11389 2.83333C8.27398 2.67307 8.27385 2.41336 8.11361 2.25326C7.95335 2.09314 7.69364 2.0933 7.53354 2.25354L6.75146 3.03641C6.59136 3.19668 6.5915 3.45639 6.75173 3.61648C6.83182 3.69649 6.93671 3.73647 7.04163 3.73647C7.14655 3.73647 7.25171 3.69641 7.3318 3.61621Z" fill="#505050" />
										</svg>
									</i>
								</p>
							</div>
						</Link>
					}
				</div>
				<Link to={handleLinkModuleTab(type, module)} onClick={() => setModuleTab(setTab, type, module)} className="cp w100 h100" style={{ minHeight: '133px' }}>
					<div className="cp ttc pr fs1rem fw500 c15 lh2 df aic zi1 w100">
						<div className='df fw500 fs1-25rem c15 aifs w100'>
							<div className='df aic cp w100'>
								<i className='icon-diligenstia mr1rem'>
									<img className="w40px h40px" src={require(`../../../images/advisor-dashboard/logo-advisor.svg`)} alt="" />
								</i>
								<p className='fs1rem fw500 c15 lh2 title-box-feature w100'>
									{
										company?.['subscription'] == 'Pro' &&
										(type == 'repository' || type == `business-audit-${company?.['testType']}` || type == 'warranty-disclosure' || type.includes("directors-questionnaire")) &&
										<button onClick={() => document.getElementById('dashboard-required-star').scrollIntoView({ behavior: "smooth" })} type='button' className='cp bg1 icon'>
											<img src={require('../../../images/landingpage/gold-star-icon.png')} alt="star-img" />
										</button>
									}
									{title === "InvestorMatch" ? <div>
										<p className='c15 fs11px fw400'>OneForm</p>
										<span className='cp c4-hover'>Investor<span className='c4'>Match</span></span>
									</div> : <p className='df aic jcsb w100'>
										<span className='cp c4-hover'>{title}</span>
										<span className='c59 ttu'>{subscription}</span>
									</p>}

								</p>
							</div>
						</div>
					</div>
					{company?.['subscription'] == 'Pro' && type == "unaudited-certification" && linearProgress &&
						<div className="bottom-certification w70 mt1rem df jcfs fww pr" style={{ top: '16px' }}>
							<img className="w100" src={require(`../../../images/advisor-dashboard/banner-certificate-bottom.png`)} alt="" />
							<div className="progress-dashboard-horizantal" role="progressbar" id="progressBar">
								<div className="progress-bar progress-bar-striped progress-bar-animated" id="innerBar" style={{ width: `${linearProgress}%` }}></div>
							</div>
						</div>
					}
					{type.includes("directors-questionnaire") &&
						<span className='cp step-box text-gradient' style={{ fontSize: '12px', right: '50px' }}>({directors.length}) - Directors</span>
					}
					{(user['role'] == 'Guest' && step) && <span className='step-box'>Step {step}</span>}
					{(time && !module.stats.completionPercent) ? <><div className='df fdc aife' style={{ position: 'absolute', right: '10px', bottom: '10px' }}  >
						{
							time && <div className='df aic'>
								<img src={require('../../../images/dashboard/stopwatch.svg')} style={{ width: '14px', height: '14px' }} />
								<span style={{ fontSize: '14px' }}>{time}</span>
							</div>
						}
					</div></> : moduleCertification && <>
						{(title === "InvestorMatch" && !module.stats.completionPercent) ? <></> : (moduleCertification == 'None') && <div className="ribbon ribbon-bottom-right grey"><img className="mr0-5rem" style={{ width: '70px' }} src={require(`../../../images/insufficient-score.png`)} alt="" /></div>}
						{moduleCertification == 'Bronze' && <div className="trophy-cup ribbon ribbon-bottom-right bronze"><img className="h4rem" src={require(`../../../images/bronze-cup.png`)} alt="" /></div>}
						{moduleCertification == 'Silver' && <div className="trophy-cup ribbon ribbon-bottom-right silver"><img className="h4rem" src={require(`../../../images/silver-cup.png`)} alt="" /></div>}
						{moduleCertification == 'Gold' && <div className="trophy-cup ribbon ribbon-bottom-right gold"><img className="h4rem" src={require(`../../../images/gold-cup.png`)} alt="" /></div>}
					</>}
					{(information && !module.stats.completionPercent) ? <><span className="bottom-features df jcsb aife mt1rem tal pa c15 op50" style={{ bottom: '15px' }}>
						<p className='w70 tal c15 pr' style={{ fontSize: '0.75rem' }}>{information}</p>
					</span></> : type == "directors-questionnaire" && directorsStats
						? <span className="tooltip-area style-two pa df aic jcsb w90">
							<div className="percentage-area df jcc aic mb1rem">
								{showPercent(directorsStats?.completionPercent || 0, 'Module Complete', title)}
								{showPercent(directorsStats?.riskPercent || 0, 'Module Risk', title)}
							</div>
						</span>
						: module?.stats && <>{
							type !== "kpi-report" && (
								<>
									{
										(title === "InvestorMatch" && !module.stats.completionPercent) ? <p className='c70'>One application from to match <br></br>against our investor database</p> :
											<span className="cp tooltip-area style-two pa df aic jcsb w90" style={type.includes("directors-questionnaire") ? { bottom: "30px" } : {}}>
												<div className={`percentage-area df jcc aic ${user['role'] == 'Guest' && ' mb1rem'}`}>
													{title === "InvestorMatch" ? <>
														<div className='df aic'>
															<div className='df fdc aifs jcfs px1rem '>
																<p className={`${strongCount > 0 ? 'c34' : 'c28'} fs2rem fw600 lh1 w100 tal`}>{strongCount}</p>
																<p className='c15 fs0-75rem w4rem db fw300' style={{ lineHeight: '1.2' }}>Strong <br></br>Matches</p>
															</div>
															<div className='df fdc aifs jcfs p0rem'>
																<p className={`${mediumCount > 0 ? 'c75' : 'c28'} fs2rem fw600 lh1 w100 tal`}>{mediumCount}</p>
																<p className='c15 fs0-75rem w4rem db fw300' style={{ lineHeight: '1.2' }}>Medium <br></br>Matches</p>
															</div>
														</div>
													</> : <>
														{showPercent(module.stats.completionPercent || 0, 'Module Complete', title)}
														{(type != 'repository' && type != 'investor-lead') && showPercent(module.stats.riskPercent || 0, 'Module Risk', title)}
													</>}
												</div>
											</span>
									}
								</>
							)}
						</>}
					{type == "kpi-report" && <>
						<span className="bottom-features df jcsb aife mt1rem tal pa c15 op50" style={{ bottom: '15px' }}>
							<p className='w100 tal c15 pr' style={{ fontSize: '0.75rem' }}>AI generated & bespoked to<br />your company</p>
						</span>
						{isReady && <span className='br50px c1 pa' style={{ background: '#15D757', right: '15px', bottom: '15px', padding: '2px 10px' }}>
							<img className='mr0-5rem' src={require('../../../images/dashboard/whiteCheck.png')} alt="Ready" />
							Ready
						</span>}
					</>
					}
				</Link>
				{module
					? (type !== "kpi-report") && moduleTooltip(module['expansionPack'] ? Object.values(module['sections'][0]['subFolders']) : module.sections, module)
					: title == "Directors Questionnaire" && directorModuleTooltip(directors)
				}
				<div className='report-bottom-area'>
					{(permissionLevel == "ALL" || permissionLevel == "DASHBOARD & REPORT") && <>
						{!(['unaudited-certification', 'kpi-report', 'directors-questionnaire'].includes(type)) && user?.['role'] !== 'Guest' && <>
							{/* Module Report */}
							<Link to={(module?.stats?.completionPercent > 0 && type != 'investor-lead') && `#${type}-report`} onClick={() => { module?.stats?.completionPercent > 0 && setReportTab(type) }} className={`w2rem h2rem df jcc aic br100 c1 report-btn pr ${module?.stats?.completionPercent > 0 ? ' bg3 bg4-hover' : ' bg12'}`}>R
								<div className='overlay-box'>
									<p className='c15 df aic'>
										Module Report
										<i className='icon ml0-5rem'>
											<svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M11.1659 7.57457V10.6723C11.1659 12.3866 9.77122 13.7812 8.05696 13.7812H6.37657C5.23568 13.7812 4.25418 13.2147 3.68373 12.2268L1.77282 8.91715C1.58918 8.5978 1.69863 8.18893 2.01661 8.00543C2.73387 7.59183 3.61212 7.81624 4.0161 8.51635L4.50071 9.35517V4.7025C4.50071 4.15516 4.94565 3.70986 5.49252 3.70986C6.0394 3.70986 6.48516 4.15516 6.48516 4.7025V6.49204C6.65337 6.36068 6.86477 6.28236 7.09416 6.28236C7.42275 6.28236 7.71453 6.44282 7.8953 6.68954C8.07593 6.44282 8.36747 6.28236 8.69579 6.28236C9.10548 6.28236 9.45802 6.53185 9.6094 6.88688C9.7748 6.7358 9.9947 6.6436 10.2357 6.6436C10.7486 6.6436 11.1659 7.06125 11.1659 7.57457ZM5.90232 2.44038V0.628906C5.90232 0.402391 5.71868 0.21875 5.49217 0.21875C5.26565 0.21875 5.08201 0.402391 5.08201 0.628906V2.44035C5.08201 2.66686 5.26565 2.8505 5.49217 2.8505C5.71868 2.8505 5.90232 2.66689 5.90232 2.44038ZM3.46769 4.87501C3.46769 4.64849 3.28405 4.46485 3.05753 4.46485H1.24609C1.01958 4.46485 0.835938 4.64849 0.835938 4.87501C0.835938 5.10152 1.01958 5.28516 1.24609 5.28516H3.05753C3.28405 5.28516 3.46769 5.10152 3.46769 4.87501ZM10.1492 4.87501C10.1492 4.64849 9.96558 4.46485 9.73906 4.46485H7.92759C7.70108 4.46485 7.51744 4.64849 7.51744 4.87501C7.51744 5.10152 7.70108 5.28516 7.92759 5.28516H9.73906C9.96558 5.28516 10.1492 5.10152 10.1492 4.87501ZM4.23261 3.61648C4.39287 3.45639 4.39298 3.19668 4.23288 3.03641L3.45079 2.25354C3.2907 2.0933 3.03098 2.09316 2.87072 2.25326C2.71046 2.41336 2.71035 2.67307 2.87045 2.83333L3.65254 3.61621C3.73265 3.69638 3.83765 3.7365 3.94271 3.7365C4.04776 3.7365 4.15254 3.69646 4.23261 3.61648ZM7.3318 3.61621L8.11389 2.83333C8.27398 2.67307 8.27385 2.41336 8.11361 2.25326C7.95335 2.09314 7.69364 2.0933 7.53354 2.25354L6.75146 3.03641C6.59136 3.19668 6.5915 3.45639 6.75173 3.61648C6.83182 3.69649 6.93671 3.73647 7.04163 3.73647C7.14655 3.73647 7.25171 3.69641 7.3318 3.61621Z" fill="#505050" />
											</svg>
										</i>
									</p>
								</div>
							</Link>

						</>}
					</>}
				</div>
			</div>

			{/* No Registration Report Popup */}
			<ModalWrapper modalType={"noRegistrationReport"} modalCardClasses="maw800px">
				<>
					<h2 className="c15 fw600 tac w100">Registration</h2>
					<div className='df fdc jcc my1rem'>
						<div className='tac'>
							<p className='my0-5rem c15 op70 fs14px'>To view or store/print your company reports, <br /> you need to register (no credit card required).</p>
						</div>
						<div className='df jcc mt1rem'>
							<Link to='/register' onClick={() => dispatch(toggleModal({ open: '' }))} className="shadow bsbb jcc df fdc aic fw500  px1rem c1 bg4 br19px cp" style={{ width: '44%', paddingTop: '4px', paddingBottom: '4px' }}>
								<span>Register for FREE</span>
								<span className="fw400 c21 df fdc" style={{ marginTop: "-2px", fontSize: '10px', lineHeight: '15px', fontStyle: 'italic' }}>No credit card required</span>
							</Link>
						</div>
						<p className='mt1-5rem c15 op70 fs11px fsi df aic tac lh1'> <LockOutlined style={{ color: "#4A67FF", fontSize: '12px', marginRight: '4px' }} /> Get access to more features. Store your test results plus FREE bonus KPI Report, all bespoked by AI to your company.</p>
					</div>
				</>
			</ModalWrapper>

		</Fragment>
	);
}